import React, { useEffect, useId, useState } from 'react';
import apiService from '../../services/ApiService';
import { useToast } from '../../contexts/ToastContext';
import {
    AdminTool, AUDIENCES, TYPE_FIELD, ToolIcon, hasBrandIcon, optionLabel, segmentState,
} from '../tools-catalog/catalogModel';
import { languageLabel } from '../../constants/languages';
import type { SectionId } from './sections';
import ReadinessCard from './ReadinessCard';

export interface ToolOverview {
    createdAt: string;
    opensLast30Days: number;
    usersLast30Days: number;
    lastUsedAt?: string | null;
    errors24h: number;
    warnings24h: number;
    openReports: number;
}

const DESCRIPTION_MAX = 160;
const DESCRIPTION_MIN = 40;

interface Draft { name: string; description: string; toolType: string; audience: string; displayOrder: number }
const draftOf = (tool: AdminTool): Draft => ({
    name: tool.name, description: tool.description ?? '', toolType: tool.toolType, audience: tool.audience ?? 'All', displayOrder: tool.displayOrder,
});

interface Props {
    tool: AdminTool;
    overview: ToolOverview | null;
    onSaved: (patch: Partial<AdminTool>) => void;
    onGo: (section: SectionId) => void;
    onDirtyChange: (dirty: boolean) => void;
}

/** Panoramica: lista "Pronto per gli utenti", dati del tool, anteprima nel portale, numeri recenti. */
const OverviewSection: React.FC<Props> = ({ tool, overview, onSaved, onGo, onDirtyChange }) => {
    const { showToast } = useToast();
    const [draft, setDraft] = useState<Draft>(() => draftOf(tool));
    const [saving, setSaving] = useState(false);
    const ids = { name: useId(), description: useId(), type: useId(), audience: useId(), order: useId() };

    const saved = draftOf(tool);
    const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
    useEffect(() => { onDirtyChange(dirty); }, [dirty, onDirtyChange]);

    const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft(prev => ({ ...prev, [key]: value }));
    const descriptionLength = draft.description.trim().length;
    const nameMissing = !draft.name.trim();
    const tooLong = draft.description.length > DESCRIPTION_MAX;

    const save = async () => {
        if (nameMissing || tooLong) return;
        setSaving(true);
        try {
            const patch = { ...draft, name: draft.name.trim(), description: draft.description.trim() };
            await apiService.adminUpdateTool(tool.id, patch);
            onSaved(patch);
            showToast('Dati del tool salvati', patch.name, 'success');
        } catch (err) {
            const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Riprova';
            showToast('Dati non salvati', message, 'danger');
        } finally {
            setSaving(false);
        }
    };

    const checks = readiness(tool, overview, tool.description ?? '');

    return (
        <div className="td-overview">
            <ReadinessCard checks={checks} onAct={check => {
                const target = (check as Check).target;
                if (target === 'description') document.getElementById(ids.description)?.focus();
                else onGo(target);
            }} />

            <section className="td-card td-data" aria-labelledby="td-data-title">
                <h2 id="td-data-title" className="td-card__title">Dati del tool</h2>
                <div className="td-field">
                    <label htmlFor={ids.name}>Nome</label>
                    <input id={ids.name} className="form-control" value={draft.name} onChange={e => set('name', e.target.value)}
                        aria-invalid={nameMissing} aria-describedby={nameMissing ? `${ids.name}-err` : undefined} />
                    {nameMissing && <span id={`${ids.name}-err`} className="td-field__error">Il nome serve: è quello che vedono gli utenti.</span>}
                </div>
                <div className="td-field td-data__description">
                    <div className="td-field__row">
                        <label htmlFor={ids.description}>Descrizione breve</label>
                        <span className={`td-field__count${tooLong ? ' is-over' : ''}`}>{draft.description.length} / {DESCRIPTION_MAX}</span>
                    </div>
                    <textarea id={ids.description} className="form-control" rows={3} value={draft.description}
                        onChange={e => set('description', e.target.value)} aria-invalid={tooLong}
                        aria-describedby={`${ids.description}-hint`} />
                    <span id={`${ids.description}-hint`} className={`td-field__hint${tooLong ? ' td-field__error' : ''}`}>
                        {tooLong
                            ? `Accorciala di ${draft.description.length - DESCRIPTION_MAX} caratteri: nel catalogo verrebbe tagliata.`
                            : descriptionLength < DESCRIPTION_MIN
                                ? 'Compare sotto il nome nel catalogo del portale: una frase che dica cosa fa il tool.'
                                : 'Compare sotto il nome nel catalogo del portale. La descrizione lunga si scrive in Contenuti.'}
                    </span>
                </div>
                <div className="td-data__grid">
                    <div className="td-field">
                        <label htmlFor={ids.type}>Tipo</label>
                        <select id={ids.type} className="form-select" value={draft.toolType} onChange={e => set('toolType', e.target.value)}>
                            {TYPE_FIELD.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    <div className="td-field">
                        <label htmlFor={ids.audience}>Pensato per</label>
                        <select id={ids.audience} className="form-select" value={draft.audience} onChange={e => set('audience', e.target.value)}>
                            {AUDIENCES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                        </select>
                    </div>
                    <div className="td-field">
                        <label htmlFor={ids.order}>Posizione nel catalogo</label>
                        <input id={ids.order} type="number" min={0} className="form-control" value={draft.displayOrder}
                            onChange={e => set('displayOrder', Number(e.target.value) || 0)} />
                    </div>
                </div>
            </section>

            <aside className="td-side">
                <section className="td-card" aria-labelledby="td-preview-title">
                    <h2 id="td-preview-title" className="td-card__title">Come appare nel portale</h2>
                    <div className="td-preview">
                        <ToolIcon tool={tool} size={44} />
                        <div className="td-preview__text">
                            <strong>{draft.name || 'Nome del tool'}</strong>
                            <span>{draft.description || 'La descrizione breve compare qui.'}</span>
                            <span className="td-preview__chip">{optionLabel(TYPE_FIELD, draft.toolType)}</span>
                        </div>
                    </div>
                </section>

                <section className="td-card" aria-labelledby="td-recent-title">
                    <div className="td-card__row">
                        <h2 id="td-recent-title" className="td-card__title">Ultime 24 ore</h2>
                        <button type="button" className="td-link" onClick={() => onGo('qualita')}>Qualità</button>
                    </div>
                    {overview ? <>
                        <div className="td-metrics">
                            <div className="td-metric td-metric--danger"><strong>{overview.errors24h}</strong><span>{overview.errors24h === 1 ? 'errore' : 'errori'}</span></div>
                            <div className="td-metric td-metric--warning"><strong>{overview.warnings24h}</strong><span>{overview.warnings24h === 1 ? 'avviso' : 'avvisi'}</span></div>
                            <div className="td-metric"><strong>{overview.openReports}</strong><span>{overview.openReports === 1 ? 'segnalazione' : 'segnalazioni'}</span></div>
                        </div>
                        <p className="td-card__note">
                            {overview.opensLast30Days === 0
                                ? 'Nessuna apertura negli ultimi 30 giorni.'
                                : `Aperto ${overview.opensLast30Days} ${overview.opensLast30Days === 1 ? 'volta' : 'volte'} da ${overview.usersLast30Days} ${overview.usersLast30Days === 1 ? 'utente' : 'utenti'} negli ultimi 30 giorni.`}
                        </p>
                    </> : <p className="td-card__note">Caricamento…</p>}
                </section>
            </aside>

            {dirty && (
                <div className="td-savebar" role="region" aria-label="Modifiche non salvate">
                    <span>Modifiche non salvate ai dati del tool</span>
                    <button type="button" className="td-savebar__ghost" onClick={() => setDraft(saved)} disabled={saving}>Annulla</button>
                    <button type="button" className="td-savebar__primary" onClick={() => void save()} disabled={saving || nameMissing || tooLong}>
                        {saving ? 'Salvo…' : 'Salva'}
                    </button>
                </div>
            )}
        </div>
    );
};

interface Check { title: string; detail: string; done: boolean; action?: string; target: SectionId | 'description' }

const languagesOf = (tool: AdminTool, kind: string, state: 'published' | 'draft') =>
    (tool.contentSlots ?? []).filter(s => s.kind === kind && (state === 'published' ? s.published : s.draft)).map(s => languageLabel(s.language));

const contentCheck = (tool: AdminTool, kind: string, title: string): Check => {
    const state = segmentState(tool, kind);
    const published = languagesOf(tool, kind, 'published');
    const drafts = languagesOf(tool, kind, 'draft');
    const pdf = kind === 'guide' ? tool.guideLanguages.map(languageLabel) : [];
    if (state === 'outdated')
        return { title, detail: 'Una traduzione è rimasta indietro rispetto all’italiano', done: false, action: 'Aggiorna', target: 'contenuti' };
    if (state === 'draft')
        return { title, detail: `Bozza in ${drafts.join(', ')}, non ancora online`, done: false, action: 'Rivedi e pubblica', target: 'contenuti' };
    if (state === 'published') {
        const where = published.length ? `Pubblicata in ${published.join(', ')}` : `PDF in ${pdf.join(', ')}`;
        return { title, detail: where, done: true, target: 'contenuti' };
    }
    return { title, detail: 'Non ancora creata', done: false, action: 'Crea', target: 'contenuti' };
};

function readiness(tool: AdminTool, overview: ToolOverview | null, description: string): Check[] {
    const length = description.trim().length;
    const icon = hasBrandIcon(tool.code) || tool.imageRoles?.includes('tab');
    const issues = overview ? overview.errors24h + overview.openReports : 0;
    return [
        icon
            ? { title: 'Icona', detail: 'Visibile nel catalogo e nell’intestazione del tool', done: true, target: 'aspetto' }
            : { title: 'Icona', detail: 'Nel catalogo compaiono le iniziali', done: false, action: 'Carica', target: 'aspetto' },
        length >= DESCRIPTION_MIN && length <= DESCRIPTION_MAX
            ? { title: 'Descrizione breve', detail: 'Pronta per il catalogo', done: true, target: 'description' }
            : { title: 'Descrizione breve', detail: length ? 'Troppo corta per spiegare il tool' : 'Manca', done: false, action: 'Scrivi', target: 'description' },
        contentCheck(tool, 'card', 'Scheda nel portale'),
        contentCheck(tool, 'publicPage', 'Pagina pubblica'),
        contentCheck(tool, 'guide', 'Guida utente'),
        !overview
            ? { title: 'Nessun problema aperto', detail: 'Verifica in corso…', done: false, target: 'qualita' }
            : issues === 0
                ? { title: 'Nessun problema aperto', detail: 'Nessun errore nelle ultime 24 ore e nessuna segnalazione', done: true, target: 'qualita' }
                : {
                    title: 'Nessun problema aperto',
                    detail: [overview.errors24h ? `${overview.errors24h} ${overview.errors24h === 1 ? 'errore' : 'errori'} nelle ultime 24 ore` : '',
                        overview.openReports ? `${overview.openReports} ${overview.openReports === 1 ? 'segnalazione aperta' : 'segnalazioni aperte'}` : '']
                        .filter(Boolean).join(', '),
                    done: false, action: 'Vai alla qualità', target: 'qualita',
                },
    ];
}

export default OverviewSection;
